import { useMemo } from 'react'
import { Image } from '@/atoms/Image'
import { NamedColor } from '@/constants/types'
import { TheatricalReleaseObject } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { CRITIC_IMAGES } from '../ShowtimesView/components/ShowtimeDetailsPanel/ShowtimeDetailsPanel'
import { GuildScoreToolTip } from '../ShowtimesView/components/ToolTips/GuildScoreToolTip'
import { getHoursMinutes } from '../utils'

interface UseTheatricalMetadataProps {
  rating?: string | null
  movieDuration?: number | null
  projectGuildScore?: number | null
  critics?: TheatricalReleaseObject['critics']
  guildScoreOptions?: {
    icon?: React.ReactNode
    triggerTextClassName?: string
  }
  iconOptions?: {
    guildIconColor?: NamedColor
  }
}

interface MetadataItem {
  id: string
  type: 'rating' | 'duration' | 'guild-score' | 'tomato-score' | 'audience-score' | 'imdb-score'
  icon?: React.ReactNode
  content: React.ReactNode
  value: string | number | null | undefined
}

export const useTheatricalMetadata = ({
  rating,
  movieDuration,
  projectGuildScore,
  critics,
  iconOptions,
  guildScoreOptions,
}: UseTheatricalMetadataProps): MetadataItem[] => {
  const { locale } = useLocale()

  const rottenTomatoesCritic = critics?.find((critic) => critic?.company === 'Rotten Tomatoes')
  const audienceScore = rottenTomatoesCritic?.scores?.find((score) => score?.name === 'Audience')?.value ?? 0
  const tomatoScore = rottenTomatoesCritic?.scores?.find((score) => score?.name === 'Tomatometer')?.value ?? 0
  const isVerifiedHot = Boolean(rottenTomatoesCritic?.verifiedHot)
  const imdbCritic = critics?.find((critic) => critic?.company === 'IMDB')
  const imdbScore = imdbCritic?.scores?.find((score) => score?.name === 'Rating')?.floatValue ?? 0

  const metadataItems = useMemo(() => {
    const items: MetadataItem[] = [
      {
        id: '0',
        content: rating ?? null,
        type: 'rating',
        value: rating,
      },
      {
        id: '1',
        content: movieDuration ? getHoursMinutes(movieDuration / 60, locale) : null,
        type: 'duration',
        value: movieDuration,
      },
      {
        id: '2',
        type: 'guild-score',
        content: projectGuildScore ? (
          <GuildScoreToolTip
            icon={guildScoreOptions?.icon}
            iconSize={16}
            triggerTextClassName={guildScoreOptions?.triggerTextClassName}
            iconColor={iconOptions?.guildIconColor ?? 'core-gray-700'}
            buttonClassName="!p-0 !border-0"
            guildScore={projectGuildScore}
          />
        ) : null,
        value: projectGuildScore,
      },
      {
        id: '3',
        type: 'tomato-score',
        icon: <Image className="rounded-xl" alt="" src={CRITIC_IMAGES['TOMATO']} width={16} height={16} />,
        content: tomatoScore ? `${tomatoScore}%` : null,
        value: tomatoScore,
      },
      {
        id: '4',
        type: 'audience-score',
        icon: (
          <Image
            className="rounded-xl"
            alt=""
            src={isVerifiedHot ? CRITIC_IMAGES['VERIFIED_HOT'] : CRITIC_IMAGES['POPCORN']}
            width={16}
            height={16}
          />
        ),
        content: audienceScore ? `${audienceScore}%` : null,
        value: audienceScore,
      },
      {
        id: '5',
        type: 'imdb-score',
        icon: <Image className="rounded-xl shadow-xl" alt="" src={CRITIC_IMAGES['IMDB']} width={16} height={16} />,
        content: imdbScore ? `${imdbScore}` : null,
        value: imdbScore,
      },
    ]

    return items.filter((item): item is MetadataItem => {
      if (!item.content) return false
      if (['tomato-score', 'audience-score'].includes(item.type))
        return typeof item.value === 'number' && item.value >= 70
      if (['imdb-score'].includes(item.type)) return typeof item.value === 'number' && item.value >= 7
      return true
    })
  }, [
    audienceScore,
    isVerifiedHot,
    locale,
    movieDuration,
    projectGuildScore,
    rating,
    tomatoScore,
    iconOptions?.guildIconColor,
    guildScoreOptions,
    imdbScore,
  ])

  return metadataItems
}
